import { useCallback } from "react";

import { log as axiomLog } from "next-axiom";

import useMe from "@/hooks/useMe";
import useMerchant from "@/hooks/useMerchant";

type ReportError = {
  message?: string;
  stack?: string;
};

/**
 * A custom hook that provides an error reporting mechanism using Axiom logging.
 *
 * @param site The identifier for the site, used as part of the logging context.
 *
 * @returns An object containing the `log` function for error reporting.
 */
export const useAxiomErrorReporter = (site: string) => {
  const { me } = useMe();
  const { id: merchantId } = useMerchant();

  const log = useCallback(
    <TError extends ReportError>(
      component: string,
      { message, stack, ...context }: TError
    ) => {
      axiomLog.error(`${site}:${component}`, {
        userId: me?.id,
        merchantId,
        error: message,
        stack,
        ...context,
      });
    },
    [me?.id, merchantId, site]
  );

  return { log };
};
